import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Search, Check, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Floating } from '../shell/floating.js'

/**
 * SIRH LOT 4.5 — PRIMITIVES DE SÉLECTION PREMIUM (design-system §93-97).
 *
 * Réutilisables par tout Gestion, montées en PORTAL via `Floating` (jamais de
 * menu blanc natif, jamais découpé par un parent en overflow). Dark, recherche
 * intégrée, clavier complet (flèches / Entrée / Échap), focus visible.
 *
 *   · Combobox        — sélection unique recherchable, avec « accès rapide » ;
 *   · SegmentedControl — bascule courte (Jour|Semaine|Mois, Employés|Postes) ;
 *   · DatePicker       — calendrier mois + raccourcis (aujourd'hui, ±semaine).
 */

export interface ComboItem {
  value: string; label: string; hint?: string; dot?: string
  /** Mots reconnus par la recherche sans être affichés (« ampoule » → Lumière). */
  keywords?: string
}

export function Combobox(props: {
  value: string | null
  items: ComboItem[]
  onChange: (value: string | null) => void
  placeholder?: string
  ariaLabel?: string
  /** Valeurs mises en avant en tête (accès rapide, §30). */
  recent?: string[]
  clearable?: boolean
  disabled?: boolean
  /** Icône de tête optionnelle (rendue dans le trigger). */
  leading?: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [focusIdx, setFocusIdx] = useState(0)
  const trigger = useRef<HTMLButtonElement>(null)
  const listId = useId()
  const current = props.items.find((i) => i.value === props.value)

  const ordered = useMemo(() => {
    const recent = props.recent ?? []
    const score = (v: string) => { const i = recent.indexOf(v); return i === -1 ? 999 : i }
    return [...props.items].sort((a, b) => score(a.value) - score(b.value))
  }, [props.items, props.recent])
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const sansAccent = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    const n = sansAccent(s)
    return s ? ordered.filter((i) => sansAccent(i.label).includes(n) || sansAccent(i.hint ?? '').includes(n) || sansAccent(i.keywords ?? '').includes(n)) : ordered
  }, [ordered, q])

  useEffect(() => { if (open) { setQ(''); setFocusIdx(0) } }, [open])

  const pick = (v: string | null) => { props.onChange(v); setOpen(false); trigger.current?.focus() }
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((i) => Math.min(filtered.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); const it = filtered[focusIdx]; if (it) pick(it.value) }
  }

  return (
    <div className={`lc-combo${props.className ? ` ${props.className}` : ''}`}>
      <button
        ref={trigger} type="button" className="lc-combo__trigger" disabled={props.disabled}
        aria-haspopup="listbox" aria-expanded={open} aria-label={props.ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {props.leading ? <span className="lc-combo__lead" aria-hidden="true">{props.leading}</span> : null}
        <span className="lc-combo__value">
          {current?.dot ? <span className={`lc-dot ${current.dot}`} aria-hidden="true" /> : null}
          {current ? current.label : <span className="lc-combo__ph">{props.placeholder ?? 'Choisir…'}</span>}
        </span>
        {props.clearable && current && !props.disabled
          ? <span className="lc-combo__clear" role="button" aria-label="Effacer" onClick={(e) => { e.stopPropagation(); pick(null) }}><X size={13} /></span>
          : null}
        <ChevronDown size={15} className={`lc-combo__chev${open ? ' lc-combo__chev--open' : ''}`} aria-hidden="true" />
      </button>
      <Floating open={open} anchorRef={trigger} matchAnchorWidth onOutside={() => setOpen(false)} className="lc-combo__panel" role="listbox" id={listId}>
        <div className="lc-combo__search">
          <Search size={15} aria-hidden="true" />
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setFocusIdx(0) }} onKeyDown={onKey}
            placeholder="Rechercher…" aria-label="Rechercher" />
        </div>
        <ul className="lc-combo__list">
          {props.clearable ? (
            <li className={`lc-combo__opt lc-combo__opt--none`} role="option" aria-selected={props.value === null} onClick={() => pick(null)}>
              <span className="lc-combo__ph">{props.placeholder ?? 'Aucun'}</span>
              {props.value === null ? <Check size={14} /> : null}
            </li>
          ) : null}
          {filtered.length === 0 ? <li className="lc-combo__empty">Aucun résultat.</li> : filtered.map((it, i) => (
            <li key={it.value} role="option" aria-selected={it.value === props.value}
              className={`lc-combo__opt${i === focusIdx ? ' lc-combo__opt--focus' : ''}`}
              onMouseEnter={() => setFocusIdx(i)} onClick={() => pick(it.value)}>
              <span className="lc-combo__optmain">
                {it.dot ? <span className={`lc-dot ${it.dot}`} aria-hidden="true" /> : null}
                <span className="lc-combo__optlabel">{it.label}</span>
                {it.hint ? <span className="lc-combo__opthint">{it.hint}</span> : null}
              </span>
              {it.value === props.value ? <Check size={14} aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      </Floating>
    </div>
  )
}

export function SegmentedControl<T extends string>(props: {
  value: T
  options: { value: T; label: string; icon?: ReactNode }[]
  onChange: (v: T) => void
  ariaLabel?: string
  size?: 'sm' | 'md'
}) {
  return (
    <div className={`lc-segment${props.size === 'sm' ? ' lc-segment--sm' : ''}`} role="tablist" aria-label={props.ariaLabel}>
      {props.options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === props.value}
          className={`lc-segment__opt${o.value === props.value ? ' lc-segment__opt--on' : ''}`}
          onClick={() => props.onChange(o.value)}>
          {o.icon ? <span className="lc-segment__icon" aria-hidden="true">{o.icon}</span> : null}
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* ─── DatePicker (calendrier mois + raccourcis) ─── */

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const DOW = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function ymdToday(): string { return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()) }
function addDaysYmd(ymd: string, n: number): string { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }

export function DatePicker(props: {
  /** Jour de référence affiché (ymd). */
  value: string
  onPick: (ymd: string) => void
  /** Label du déclencheur (ex. « 31 août – 6 sept. »). */
  label: string
  /** Bornes de la sélection active à surligner (ymd inclus). */
  highlightFrom?: string
  highlightTo?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const [cursor, setCursor] = useState(() => props.value.slice(0, 7)) // YYYY-MM
  useEffect(() => { if (open) setCursor(props.value.slice(0, 7)) }, [open, props.value])

  const [yy, mm] = cursor.split('-').map(Number) as [number, number]
  const first = new Date(Date.UTC(yy, mm - 1, 1))
  const startOffset = (first.getUTCDay() + 6) % 7 // 0 = lundi
  const daysInMonth = new Date(Date.UTC(yy, mm, 0)).getUTCDate()
  const today = ymdToday()

  const cells: (string | null)[] = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${cursor}-${String(d).padStart(2, '0')}`)

  const shiftMonth = (n: number) => {
    const d = new Date(Date.UTC(yy, mm - 1 + n, 1))
    setCursor(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  const pick = (ymd: string) => { props.onPick(ymd); setOpen(false) }

  return (
    <div className="lc-datepick">
      <button ref={trigger} type="button" className="lc-datepick__trigger" aria-haspopup="dialog" aria-expanded={open}
        aria-label={props.ariaLabel ?? 'Choisir une date'} onClick={() => setOpen((o) => !o)}>
        {props.label}<ChevronDown size={14} className={`lc-combo__chev${open ? ' lc-combo__chev--open' : ''}`} aria-hidden="true" />
      </button>
      <Floating open={open} anchorRef={trigger} onOutside={() => setOpen(false)} className="lc-datepick__panel" role="dialog" ariaLabel="Calendrier">
        <div className="lc-datepick__shortcuts">
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => pick(today)}>Aujourd’hui</button>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => pick(addDaysYmd(props.value, -7))}>Semaine préc.</button>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => pick(addDaysYmd(props.value, 7))}>Semaine suiv.</button>
        </div>
        <div className="lc-datepick__head">
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Mois précédent" onClick={() => shiftMonth(-1)}><ChevronLeft size={15} /></button>
          <span className="lc-datepick__month">{MONTHS[mm - 1]} {yy}</span>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Mois suivant" onClick={() => shiftMonth(1)}><ChevronRight size={15} /></button>
        </div>
        <div className="lc-datepick__grid">
          {DOW.map((d, i) => <span key={i} className="lc-datepick__dow">{d}</span>)}
          {cells.map((ymd, i) => ymd === null ? <span key={i} /> : (
            <button key={i} type="button"
              className={`lc-datepick__day${ymd === today ? ' lc-datepick__day--today' : ''}${props.highlightFrom && props.highlightTo && ymd >= props.highlightFrom && ymd <= props.highlightTo ? ' lc-datepick__day--sel' : ''}`}
              onClick={() => pick(ymd)}>{Number(ymd.slice(8))}</button>
          ))}
        </div>
      </Floating>
    </div>
  )
}
